/**
 * Image Optimizer
 * 画像読み込みを最適化してページロード時間を改善
 *
 * 機能:
 * 1. Above-the-fold画像の優先読み込み
 * 2. Below-the-fold画像の遅延読み込み
 * 3. Intersection Observer APIによる可視領域検出
 * 4. プレースホルダー表示
 * 5. WebP形式対応
 */

(function() {
  'use strict';

  // 設定
  const CONFIG = {
    // Above-the-fold画像の数（即座に読み込む）
    EAGER_IMAGE_COUNT: 12,

    // 遅延読み込みマージン（px）
    // 画像が表示される500px前から読み込み開始
    ROOT_MARGIN: '500px 0px',

    // デバッグモード
    DEBUG: false
  };

  // ログ出力（デバッグモード時のみ）
  function log(...args) {
    if (CONFIG.DEBUG) {
      console.log('[ImageOptimizer]', ...args);
    }
  }

  // ========================================
  // 1. Intersection Observer設定
  // ========================================
  let observer = null;

  function createObserver() {
    // Intersection Observer APIが利用可能かチェック
    if (!('IntersectionObserver' in window)) {
      log('Intersection Observer not supported, using fallback');
      return null;
    }

    const observerOptions = {
      root: null, // ビューポートをルートとして使用
      rootMargin: CONFIG.ROOT_MARGIN,
      threshold: 0.01 // 1%でも表示されたら読み込み開始
    };

    return new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          loadImage(img);
          obs.unobserve(img);
        }
      });
    }, observerOptions);
  }

  // ========================================
  // 2. 画像読み込み
  // ========================================
  function loadImage(img) {
    // data-src属性から実際のsrcに変換
    const dataSrc = img.dataset.src;
    const dataSrcset = img.dataset.srcset;

    if (dataSrc) {
      img.src = dataSrc;
      img.removeAttribute('data-src');
      log('Loaded image:', dataSrc);
    }

    if (dataSrcset) {
      img.srcset = dataSrcset;
      img.removeAttribute('data-srcset');
    }

    // loading="lazy"属性を追加
    img.loading = 'lazy';
    img.decoding = 'async';

    // 読み込み完了時のクラス追加
    img.addEventListener('load', () => {
      img.classList.add('v0-image-loaded');
      log('Image fully loaded:', img.src);
    }, { once: true });

    // 読み込みエラー時の処理
    img.addEventListener('error', () => {
      img.classList.add('v0-image-error');
      log('Image load error:', img.src);
    }, { once: true });
  }

  // ========================================
  // 3. Above-the-fold画像の処理
  // ========================================
  function processEagerImages() {
    // グリッドコンテナ内の最初のN枚の画像
    const gridImages = document.querySelectorAll('.v0-grid .v0-card__thumbnail');
    let processedCount = 0;

    gridImages.forEach((img, index) => {
      if (index < CONFIG.EAGER_IMAGE_COUNT) {
        // Above-the-fold: 即座に読み込み
        img.loading = 'eager';
        img.fetchpriority = 'high';
        processedCount++;
        log(`Set image ${index + 1} to eager loading`);
      } else {
        // Below-the-fold: 遅延読み込み
        img.loading = 'lazy';
        img.decoding = 'async';

        // Intersection Observerで監視
        if (observer) {
          observer.observe(img);
        }
      }
    });

    log(`Processed ${processedCount} eager images, ${gridImages.length - processedCount} lazy images`);
    return processedCount;
  }

  // ========================================
  // 4. ヘッダー・サイドバー画像の処理
  // ========================================
  function processNavigationImages() {
    // ヘッダーロゴ、サイドバーアイコンは即座に読み込み
    const navImages = document.querySelectorAll(`
      header img,
      .v0-sidebar img,
      .v0-header img
    `);

    navImages.forEach(img => {
      img.loading = 'eager';
      img.fetchpriority = 'high';
    });

    log(`Set ${navImages.length} navigation images to eager loading`);
  }

  // ========================================
  // 5. プレビュー動画サムネイルの最適化
  // ========================================
  function optimizeVideoThumbnails() {
    // 動画プレビューサムネイルは遅延読み込み
    const videoThumbs = document.querySelectorAll('.v0-card video');

    videoThumbs.forEach(video => {
      // preload="none"で自動ダウンロードを無効化
      video.preload = 'none';

      // Intersection Observerで可視領域に入ったら読み込み
      if (observer) {
        observer.observe(video);
      }

      // ホバー時に再生準備
      video.addEventListener('mouseenter', function() {
        if (this.preload === 'none') {
          this.preload = 'metadata';
        }
      }, { once: true });
    });

    log(`Optimized ${videoThumbs.length} video thumbnails`);
  }

  // ========================================
  // 6. WebP対応チェック
  // ========================================
  function checkWebPSupport() {
    return new Promise((resolve) => {
      const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoCAAEAAQAcJaQAA3AA/v3AgAA=';
      const img = new Image();

      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = webpData;
    });
  }

  async function addWebPClass() {
    const supportsWebP = await checkWebPSupport();

    if (supportsWebP) {
      document.documentElement.classList.add('webp-supported');
      log('WebP format supported');
    } else {
      document.documentElement.classList.add('no-webp');
      log('WebP format not supported, using fallback');
    }
  }

  // ========================================
  // 7. ネットワーク速度に応じた最適化
  // ========================================
  function detectConnectionSpeed() {
    // Network Information APIが利用可能かチェック
    if (!('connection' in navigator)) {
      log('Network Information API not supported');
      return 'unknown';
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const effectiveType = connection.effectiveType; // '4g', '3g', '2g', 'slow-2g'

    log('Connection type:', effectiveType);

    // 低速回線の場合は画像品質を下げる
    if (effectiveType === '2g' || effectiveType === 'slow-2g') {
      document.documentElement.classList.add('low-bandwidth');
      CONFIG.EAGER_IMAGE_COUNT = 6; // Above-the-fold画像数を削減
      log('Low bandwidth detected, reducing eager image count to 6');
    }

    return effectiveType;
  }

  // ========================================
  // 8. プログレッシブ画像読み込み
  // ========================================
  function enableProgressiveLoading() {
    // 低解像度プレースホルダーから高解像度画像への切り替え
    const progressiveImages = document.querySelectorAll('img[data-lowsrc]');

    progressiveImages.forEach(img => {
      // 低解像度画像を即座に表示
      const lowSrc = img.dataset.lowsrc;
      if (lowSrc) {
        img.src = lowSrc;
        img.classList.add('v0-image-loading');
      }

      // 高解像度画像を遅延読み込み
      const highSrc = img.dataset.src;
      if (highSrc) {
        const highResImg = new Image();
        highResImg.onload = () => {
          img.src = highSrc;
          img.classList.remove('v0-image-loading');
          img.classList.add('v0-image-loaded');
        };
        highResImg.src = highSrc;
      }
    });

    log(`Enabled progressive loading for ${progressiveImages.length} images`);
  }

  // ========================================
  // 9. パフォーマンス測定
  // ========================================
  function measureImagePerformance() {
    if (!window.performance || !window.PerformanceObserver) return;

    try {
      // 画像リソースの読み込み時間を測定
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const imageEntries = entries.filter(entry => entry.initiatorType === 'img');

        if (imageEntries.length > 0) {
          const totalTime = imageEntries.reduce((sum, entry) => sum + entry.duration, 0);
          const avgTime = totalTime / imageEntries.length;

          log('Image Performance:', {
            'Total Images': imageEntries.length,
            'Average Load Time': `${avgTime.toFixed(2)}ms`,
            'Total Time': `${totalTime.toFixed(2)}ms`
          });
        }
      });

      observer.observe({ entryTypes: ['resource'] });
    } catch (e) {
      log('Performance measurement not supported:', e);
    }
  }

  // ========================================
  // 初期化
  // ========================================
  function init() {
    log('Image Optimizer initialized');

    // WebP対応チェック
    addWebPClass();

    // ネットワーク速度検出
    detectConnectionSpeed();

    // Intersection Observer作成
    observer = createObserver();

    // DOM読み込み完了後に実行
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        processNavigationImages();
        processEagerImages();
        optimizeVideoThumbnails();
        enableProgressiveLoading();
        measureImagePerformance();
      });
    } else {
      processNavigationImages();
      processEagerImages();
      optimizeVideoThumbnails();
      enableProgressiveLoading();
      measureImagePerformance();
    }
  }

  // ========================================
  // CSSスタイル追加
  // ========================================
  function addStyles() {
    const style = document.createElement('style');
    style.textContent = `
      /* 画像読み込み中のプレースホルダー */
      .v0-image-loading {
        filter: blur(5px);
        transition: filter 0.3s ease;
      }

      .v0-image-loaded {
        filter: blur(0);
      }

      .v0-image-error {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .v0-image-error::after {
        content: '画像を読み込めませんでした';
        font-size: 12px;
        padding: 10px;
      }

      /* 低帯域幅モード */
      .low-bandwidth .v0-card__thumbnail {
        image-rendering: -webkit-optimize-contrast;
        image-rendering: crisp-edges;
      }
    `;
    document.head.appendChild(style);
  }

  // 初期化実行
  addStyles();
  init();

  // グローバルに公開（デバッグ用）
  window.ImageOptimizer = {
    config: CONFIG,
    observer,
    processEagerImages,
    detectConnectionSpeed
  };

})();
